import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createWarrantyClaim,
  fetchEligibleDevices,
  fetchWarrantyClaim,
  fetchWarrantyClaims,
} from './api';
import { WARRANTY_COPY, WARRANTY_POLICY } from './constants';
import type { WarrantyClaimsPage, WarrantyClaimSummary } from './schemas';

export const warrantyKeys = {
  all: ['warranty'] as const,
  /**
   * One key factory for both listings, discriminated by scope. `'account'` is a literal, not a
   * missing id: two keys that both ended in `undefined` would collide, and this way the cache
   * entry reads plainly in the devtools.
   */
  claims: (customerDeviceId?: string) =>
    [...warrantyKeys.all, 'claims', customerDeviceId ?? 'account'] as const,
  claim: (claimId: string) => [...warrantyKeys.all, 'claim', claimId] as const,
  eligibleDevices: () => [...warrantyKeys.all, 'eligible-devices'] as const,
};

/**
 * Warranty claims, newest first, a page at a time (113581 Lazy loading).
 *
 * **One hook, two scopes.** Pass a `customerDeviceId` for that Device's claims (113581 §A); omit it
 * for every claim across the account (113581 §B). The two endpoints return the identical shape, so
 * only the key differs and `fetchWarrantyClaims` picks the path — ADR-0019's "one
 * `useInfiniteQuery`, one response shape".
 *
 * `useInfiniteQuery` even though a Device realistically holds one to five claims: a single page
 * simply never offers a next one, which satisfies "no further requests are made and no loading
 * indicator is shown" with no branch.
 *
 * `enabled` is the panel's `open`, so a closed drawer never fetches.
 */
export function useWarrantyClaims(customerDeviceId: string | undefined, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: warrantyKeys.claims(customerDeviceId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchWarrantyClaims(pageParam, WARRANTY_POLICY.PAGE_SIZE, customerDeviceId),
    getNextPageParam: (lastPage: WarrantyClaimsPage) => lastPage.nextCursor,
    enabled,
    // `staleTime` and `refetchOnWindowFocus` are deliberately left at the app defaults: no AC here
    // forbids the list refreshing on its own, unlike `useDeviceList`, which pins both off because
    // US-112439 §14 requires a manual refresh.
  });
}

/**
 * The Devices the account-wide intake form may claim against (113319 § Select Device).
 *
 * Fetched as soon as the account-wide panel opens rather than when the form does, so it is already
 * warm on both routes into the form — the direct route and the CTA. There is no Figma frame for a
 * loading dropdown, and this is what keeps one from being needed.
 *
 * Under `warrantyKeys.all`, so raising a claim invalidates it and the just-claimed Device drops out
 * of the list without a second invalidation rule.
 */
export function useEligibleDevices(enabled: boolean) {
  return useQuery({
    queryKey: warrantyKeys.eligibleDevices(),
    queryFn: fetchEligibleDevices,
    enabled,
  });
}

/** Flatten the loaded pages into one list for the panel. */
export function flattenWarrantyClaimPages(
  pages: WarrantyClaimsPage[] | undefined,
): WarrantyClaimSummary[] {
  return (pages ?? []).flatMap((page) => page.claims);
}

/**
 * Raise one Warranty claim.
 *
 * Both toasts are the app-wide `<MutationToaster>`'s, keyed off `meta` — no toast call at the call
 * site, and no way for this mutation to ship silent. `errorMessage` deliberately overrides the
 * server's own wording, which ADR-0008 would otherwise prefer: 113319 fixes the failure copy
 * verbatim.
 *
 * The claim is invalidated rather than pushed into the cache: the panel closes on success and the
 * list refetches when it is reopened, showing the new claim as Pending.
 */
export function useRaiseWarrantyClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWarrantyClaim,
    meta: {
      successMessage: WARRANTY_COPY.RAISE_SUCCESS,
      errorMessage: WARRANTY_COPY.RAISE_FAILED,
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: warrantyKeys.all }),
  });
}

/**
 * One Warranty claim's detail (113581 §D/§E, ticket 03).
 *
 * `enabled` is the preview's `open`, so a closed panel never fetches — and because the key is the
 * claim, reopening the same claim from the list serves it from cache while the app defaults still
 * refetch it in the background. AC-H's "always reflects the latest persisted status" wants exactly
 * that, so `staleTime` and `refetchOnWindowFocus` stay at the app defaults.
 */
export function useWarrantyClaim(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: warrantyKeys.claim(claimId),
    queryFn: () => fetchWarrantyClaim(claimId),
    enabled: enabled && Boolean(claimId),
  });
}
